'use client';

import { useLocale } from '@/contexts/LocaleContext';
import { Employee, EventsSpeaker, Media, Speakers } from '@/payload-types';
import { getDictionary } from '@/utils/getDictionary';

import styles from './EventSpeakers.module.scss';

type Props = {
  speakers: Speakers;
};

export const EventSpeakers: React.FC<Props> = ({ speakers }) => {
  const locale = useLocale();
  const t = getDictionary(locale);

  if (!speakers?.speakers || speakers?.speakers.length === 0) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.label}>{t('events.speakers')}</h2>
      <div className={styles.list}>
        {speakers.speakers.map((speaker, index) => {
          const speakerImage =
            speaker.typeOfSpeaker === 'employee'
              ? ((speaker.employee as Employee).image as Media)
              : speaker.typeOfSpeaker === 'external'
                ? ((speaker.external as EventsSpeaker).image as Media)
                : null;

          const speakerName =
            speaker.typeOfSpeaker === 'employee'
              ? (speaker.employee as Employee).name
              : speaker.typeOfSpeaker === 'external'
                ? (speaker.external as EventsSpeaker).name
                : '';

          const speakerRole =
            speaker.typeOfSpeaker === 'employee'
              ? (speaker.employee as Employee).role
              : speaker.typeOfSpeaker === 'external'
                ? (speaker.external as EventsSpeaker).role
                : '';

          const speakerBio =
            speaker.typeOfSpeaker === 'employee'
              ? (speaker.employee as Employee).bio
              : speaker.typeOfSpeaker === 'external'
                ? (speaker.external as EventsSpeaker).bio
                : '';

          const speakerLinkedIn =
            speaker.typeOfSpeaker === 'employee'
              ? (speaker.employee as Employee).linkedin
              : speaker.typeOfSpeaker === 'external'
                ? (speaker.external as EventsSpeaker).linkedin
                : '';

          return (
            <div key={index} className={styles.item}>
              <div className={styles.header}>
                {speakerImage && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    loading='lazy'
                    className={styles.image}
                    src={speakerImage.sizes?.squareSmall?.url || ''}
                    alt={speakerImage.alt || ''}
                  />
                )}
                <div className={styles.speaker}>
                  {speakerName && (
                    <span className={styles.name}>{speakerName}</span>
                  )}
                  {speakerRole && (
                    <span className={styles.role}>{speakerRole}</span>
                  )}
                </div>
                {speakerLinkedIn && (
                  <a
                    className={styles.linkedin}
                    href={speakerLinkedIn}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {t('events.followOnLinkedIn')}
                    <svg
                      width='32'
                      height='33'
                      viewBox='0 0 32 33'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M0 16.0273C0 7.19079 7.16344 0.0273438 16 0.0273438C24.8366 0.0273438 32 7.19079 32 16.0273C32 24.8639 24.8366 32.0273 16 32.0273C7.16344 32.0273 0 24.8639 0 16.0273Z'
                        fill='#F3F2FF'
                      />
                      <path
                        d='M11.6 24.0273H8.2V13.3273H11.6V24.0273ZM9.9 11.8273C8.8 11.8273 8 11.0273 8 9.92734C8 8.82734 8.9 8.02734 9.9 8.02734C11 8.02734 11.8 8.82734 11.8 9.92734C11.8 11.0273 11 11.8273 9.9 11.8273ZM24 24.0273H20.6V18.2273C20.6 16.5273 19.9 16.0273 18.9 16.0273C17.9 16.0273 16.9 16.8273 16.9 18.3273V24.0273H13.5V13.3273H16.7V14.8273C17 14.1273 18.2 13.0273 19.9 13.0273C21.8 13.0273 23.8 14.1273 23.8 17.4273V24.0273H24Z'
                        fill='#2A0D5D'
                      />
                    </svg>
                  </a>
                )}
              </div>
              <div className={styles.bio}>
                {speakerBio && <div className={styles.bio}>{speakerBio}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
